// @ts-nocheck
/**
 * Birch Registry — "Add to Registry" button on product pages.
 *
 * Single-registry UX: clicking the button skips the picker entirely.
 * Behavior:
 *   1. Fetch the customer's registries.
 *   2. If none, prompt to create one (jumps to /pages/registry-create).
 *   3. If one or more, add to the most recently updated registry directly,
 *      with sensible defaults (uncategorized stage, nice-to-have, qty 1).
 *   4. Show a wishlist-style confirmation dialog with item info and a
 *      "View registry" CTA.
 *
 * Variant changes from the theme's product form update the data-variant-id
 * attribute via a `variant:selected` event listener (events.js bus).
 */
(function () {
  const state = { variantSet: null, productSet: null, registry: null, registries: [] };

  const bind = () => {
    document.querySelectorAll("[data-registry-add-trigger]").forEach(setup);
    refreshAllTriggers();
  };
  const ready = () => {
    if (!window.BirchRegistry) return setTimeout(ready, 30);
    bind();
    // Paint visibility from the cached flag first (no network) so a shopper with
    // a known active registry sees the button immediately; syncAddedState()
    // reconciles against the live list below.
    applyVisibility();
    new MutationObserver(() => { bind(); applyVisibility(); syncWhenTriggerExists(); })
      .observe(document.body, { childList: true, subtree: true });
    syncWhenTriggerExists();
  };

  // The registries fetch is the only network cost this file carries, and it buys
  // nothing until there's a trigger to reconcile against. A signed-out shopper
  // gets the login variant of the button, which deliberately carries no
  // [data-registry-add-trigger] (see snippets/registry-add-button.liquid), so
  // for them the call could only ever 401 — once per product view, in red, in
  // every console. Collection/search pages start without a trigger too and grow
  // one when a quick-add modal renders buy-buttons, which is why the observer
  // above re-checks rather than this being a one-shot test at DOMContentLoaded.
  let syncStarted = false;
  function syncWhenTriggerExists() {
    if (syncStarted || !document.querySelector("[data-registry-add-trigger]")) return;
    syncStarted = true;
    syncAddedState();
  }

  // The button is hidden unless the shopper has ≥1 active registry. The decision
  // comes from the cached BirchRegistry flag (updated on registry create/end),
  // so there's no per-page-load network cost to decide visibility.
  function applyVisibility() {
    const has = window.BirchRegistry?.hasActiveRegistryCached?.() === true;
    document
      .querySelectorAll(".registry-cta-area[data-registry-visibility]")
      .forEach((el) => { el.hidden = !has; });
  }

  // Re-render when another surface (e.g. creating/ending a registry) flips the flag.
  document.addEventListener("birch:registry-flag", applyVisibility);
  if (document.readyState === "loading")
    document.addEventListener("DOMContentLoaded", ready);
  else ready();

  // Single shared listener for theme variant changes — updates every button.
  document.addEventListener("variant:selected", (e) => {
    const v = e.detail?.variant;
    if (!v) return;
    document.querySelectorAll("[data-registry-add-trigger]").forEach((trigger) => {
      trigger.dataset.variantId = v.id;
      trigger.dataset.priceCents = v.price;
      refreshTrigger(trigger);
    });
  });

  async function syncAddedState() {
    try {
      const { API } = window.BirchRegistry;
      const { registries } = await API.list(); // also refreshes the active-registry flag
      applyVisibility();
      if (!registries?.length) return;
      // Active (non-ended) registries, most-recent first — drives the multi-
      // registry picker (#8) and the default add target.
      state.registries = registries
        .slice()
        .filter((r) => r.status !== "ended")
        .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
      const target = state.registries[0] || registries
        .slice()
        .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))[0];
      const { registry } = await API.get(target.id);
      state.registry = registry;
      state.variantSet = new Set();
      state.productSet = new Set();
      (registry.items || []).forEach((it) => {
        if (it.status === "removed") return;
        if (it.shopifyVariantId) state.variantSet.add(String(it.shopifyVariantId));
        if (it.shopifyProductId) state.productSet.add(String(it.shopifyProductId));
      });
      refreshAllTriggers();
    } catch (_) {}
  }

  function refreshAllTriggers() {
    document.querySelectorAll("[data-registry-add-trigger]").forEach(refreshTrigger);
  }

  function refreshTrigger(trigger) {
    if (!state.variantSet) return;
    const variantId = String(trigger.dataset.variantId || "");
    const productId = String(trigger.dataset.productId || "");
    const isAdded =
      (variantId && state.variantSet.has(variantId)) ||
      (!variantId && productId && state.productSet.has(productId));
    setAddedVisualState(trigger, isAdded);
  }

  function setAddedVisualState(trigger, added) {
    const labelEl = trigger.querySelector("[data-registry-add-label]");
    if (!labelEl) return;
    // IDEMPOTENT: only touch the DOM when the state actually changes. This is
    // load-bearing — a body-wide MutationObserver (see `ready`) re-runs
    // refreshAllTriggers() on every DOM change, and writing `textContent` is
    // itself a childList mutation. Unconditionally re-setting it here fed the
    // observer in an infinite loop that froze the page right after an add.
    const isAdded = trigger.dataset.state === "added";
    if (added) {
      if (isAdded) return;
      trigger.dataset.state = "added";
      labelEl.textContent = "Added to registry";
    } else if (isAdded) {
      delete trigger.dataset.state;
      labelEl.textContent = "Add to registry";
    }
  }

  function setup(trigger) {
    if (trigger.dataset.bound) return;
    trigger.dataset.bound = "1";
    trigger.addEventListener("click", () => onAddClick(trigger));
  }

  async function onAddClick(trigger) {
    const { API, showToast } = window.BirchRegistry;
    const labelEl = trigger.querySelector("[data-registry-add-label]");
    const original = labelEl.textContent;

    // Guard against a double-fire while a previous optimistic add for this
    // trigger is still settling with the server.
    if (trigger.dataset.pending === "1") return;
    trigger.dataset.pending = "1";

    // #8 — when the shopper has more than one active registry, ask which one
    // before adding. Done BEFORE the optimistic UI flip so a cancel is clean.
    let chosenRegistry = null;
    if (state.registries && state.registries.length > 1) {
      chosenRegistry = await pickRegistry(state.registries);
      if (!chosenRegistry) { delete trigger.dataset.pending; return; }
    }

    const variantId = trigger.dataset.variantId ? String(trigger.dataset.variantId) : "";
    const productId = trigger.dataset.productId ? String(trigger.dataset.productId) : "";

    // Was this product already on the registry before this click? Drives the
    // revert path — if it was, a failed merge shouldn't strip the "added" state.
    const wasAdded =
      (variantId && state.variantSet?.has(variantId)) ||
      (!variantId && productId && state.productSet?.has(productId)) ||
      false;

    // Optimistic update: flip the UI to "Added" immediately and seed the local
    // sets so refreshTrigger() stays consistent. The API call runs in parallel
    // below; if it fails we revert.
    if (state.variantSet && variantId) state.variantSet.add(variantId);
    if (state.productSet && productId) state.productSet.add(productId);
    setAddedVisualState(trigger, true);

    try {
      // If the shopper picked a registry above, add there; otherwise get-or-
      // create (a first-time shopper with no registry gets one started).
      const { registry } = chosenRegistry
        ? { registry: chosenRegistry }
        : await window.BirchRegistry.getOrCreateRegistry({
            registrantFirst: trigger.dataset.registrantFirst || "",
            registrantLast: trigger.dataset.registrantLast || "",
            customerEmail: trigger.dataset.customerEmail || "",
          });

      // Auto-classify by product title/handle so e.g. a bassinet lands in
      // "Sleep & Nursery" instead of "Other". Falls back to "uncategorized"
      // when no keyword matches.
      const stage = window.BirchRegistry.classifyStage({
        title: trigger.dataset.productTitle,
        handle: trigger.dataset.productHandle,
        tags: trigger.dataset.productTags || "",
      });

      const { item, merged } = await API.addItem(registry.id, {
        source: "shopify",
        shopifyProductId: trigger.dataset.productId,
        shopifyVariantId: trigger.dataset.variantId,
        shopifyHandle: trigger.dataset.productHandle,
        title: trigger.dataset.productTitle,
        imageUrl: trigger.dataset.productImage,
        priceCents: parseInt(trigger.dataset.priceCents, 10) || null,
        currency: trigger.dataset.currency || "USD",
        stage,
        priority: "nice_to_have",
        quantityWanted: 1,
      });

      delete trigger.dataset.pending;

      if (merged) {
        showToast(`Quantity increased to ${item.quantityWanted}`);
        return;
      }

      openAddedDialog({
        registry,
        item: {
          title: trigger.dataset.productTitle,
          imageUrl: trigger.dataset.productImage,
          priceCents: parseInt(trigger.dataset.priceCents, 10) || null,
          currency: trigger.dataset.currency || "USD",
        },
      });
    } catch (e) {
      delete trigger.dataset.pending;

      // Revert the optimistic update — but only if the product wasn't already
      // on the registry before this click.
      if (!wasAdded) {
        if (state.variantSet && variantId) state.variantSet.delete(variantId);
        if (state.productSet && productId) state.productSet.delete(productId);
        setAddedVisualState(trigger, false);
        labelEl.textContent = original;
      }

      if (e.status === 401) {
        if (confirm("Please sign in to use the registry. Go to login?"))
          location.href = `/customer_authentication/login?return_to=${encodeURIComponent(location.pathname)}`;
      } else {
        showToast("Something went wrong — couldn't add to your registry. Please try again.");
      }
    }
  }

  /**
   * #8 — registry chooser shown when the shopper has more than one active
   * registry. Resolves with the chosen registry object, or null if cancelled.
   */
  function pickRegistry(registries) {
    const { escapeHtml } = window.BirchRegistry;
    return new Promise((resolve) => {
      const backdrop = document.createElement("div");
      backdrop.className = "registry-modal-backdrop";
      const rows = registries
        .map(
          (r) => `
          <button type="button" class="registry-pick__row" data-pick-id="${escapeHtml(r.id)}">
            <span class="registry-pick__name">${escapeHtml(r.name || "Your Registry")}</span>
            <span class="registry-pick__meta">${r._count?.items != null ? `${r._count.items} item${r._count.items === 1 ? "" : "s"}` : ""}${r.status === "draft" ? " · draft" : ""}</span>
          </button>`,
        )
        .join("");
      backdrop.innerHTML = `
        <div class="registry-modal" role="dialog" aria-modal="true" aria-label="Choose a registry" style="max-width:400px">
          <h2 style="font-size:1.05rem;margin:0 0 4px">Add to which registry?</h2>
          <p style="font-size:0.85rem;color:var(--reg-muted);margin:0 0 14px">You have ${registries.length} active registries.</p>
          <div class="registry-pick__list">${rows}</div>
          <div class="registry-modal__actions" style="margin-top:16px">
            <button type="button" class="registry-btn registry-btn--secondary" data-action="cancel">Cancel</button>
          </div>
        </div>`;
      document.body.appendChild(backdrop);
      const done = (val) => { backdrop.remove(); resolve(val); };
      backdrop.querySelector('[data-action="cancel"]').addEventListener("click", () => done(null));
      backdrop.addEventListener("click", (e) => { if (e.target === backdrop) done(null); });
      backdrop.querySelectorAll("[data-pick-id]").forEach((b) =>
        b.addEventListener("click", () => {
          const id = b.dataset.pickId;
          done(registries.find((r) => r.id === id) || null);
        }),
      );
    });
  }

  /**
   * Wishlist-style confirmation: shows the added item with an image, the
   * registry name, and CTAs to either keep browsing or jump into the registry.
   */
  function openAddedDialog({ registry, item }) {
    const { escapeHtml, formatMoney } = window.BirchRegistry;
    const backdrop = document.createElement("div");
    backdrop.className = "registry-modal-backdrop";
    backdrop.innerHTML = `
      <div class="registry-modal" role="dialog" aria-modal="true" aria-labelledby="reg-added-title" style="max-width:420px">
        <div style="display:flex;align-items:center;gap:8px;color:var(--reg-sage-dark);font-weight:600;font-size:0.875rem;margin-bottom:12px">
          <span aria-hidden="true">✓</span> Added to your registry
        </div>
        <div style="display:flex;gap:14px;align-items:flex-start">
          ${item.imageUrl
            ? `<img src="${escapeHtml(item.imageUrl)}" alt="" style="width:72px;height:72px;object-fit:cover;border-radius:8px;background:var(--reg-cream);flex-shrink:0">`
            : `<div style="width:72px;height:72px;border-radius:8px;background:var(--reg-cream);flex-shrink:0"></div>`}
          <div style="flex:1;min-width:0">
            <h2 id="reg-added-title" style="font-size:1rem;line-height:1.35;margin:0 0 4px">${escapeHtml(item.title || "Item")}</h2>
            ${item.priceCents != null
              ? `<div style="font-size:0.875rem;color:var(--reg-muted);margin-bottom:6px">${formatMoney(item.priceCents, item.currency)}</div>`
              : ""}
            <div style="font-size:0.8125rem;color:var(--reg-muted)">
              In <strong style="color:var(--reg-ink)">${escapeHtml(registry.name)}</strong>
            </div>
          </div>
        </div>
        <div class="registry-modal__actions" style="margin-top:20px">
          <button type="button" class="registry-btn registry-btn--secondary" data-action="continue">Keep shopping</button>
          <a class="registry-btn registry-btn--primary" href="/pages/registry-edit?id=${encodeURIComponent(registry.id)}">View registry</a>
        </div>
      </div>`;
    document.body.appendChild(backdrop);

    const close = () => backdrop.remove();
    backdrop.querySelector('[data-action="continue"]').addEventListener("click", close);
    backdrop.addEventListener("click", (e) => { if (e.target === backdrop) close(); });
    document.addEventListener("keydown", function onEsc(e) {
      if (e.key === "Escape") { close(); document.removeEventListener("keydown", onEsc); }
    });
  }
})();
